import { UnsupportedFunctionalityError } from '@ai-sdk/provider'
import { convertToBase64 } from '@ai-sdk/provider-utils'

import type { OpenAICompatibleChatPrompt } from './openai-compatible-api-types'
import type {
  JSONValue,
  LanguageModelV2Prompt,
  SharedV2ProviderMetadata,
} from '@ai-sdk/provider'

function getOpenAIMetadata(message: {
  providerOptions?: SharedV2ProviderMetadata
}) {
  return message?.providerOptions?.openaiCompatible ?? {}
}

function imageUrlFromData(data: unknown, mediaType: string): string {
  // AI SDK 7 adapts this v2 provider to v4, whose file data is tagged. The
  // compatibility proxy passes that v4 shape through to the v2 implementation.
  if (data && typeof data === 'object' && 'type' in data) {
    if (data.type === 'data' && 'data' in data) {
      data = data.data
    } else if (data.type === 'url' && 'url' in data) {
      // The tagged `url` may be a plain string, not only a URL instance.
      data = data.url
    }
  }

  if (data instanceof URL) return data.toString()
  if (typeof data !== 'string' && !(data instanceof Uint8Array)) {
    throw new UnsupportedFunctionalityError({
      functionality: 'image file data that is not inline bytes or a URL',
    })
  }

  // Never re-prefix something that is already addressable. `convertToBase64`
  // passes a string through unchanged (it assumes the string IS base64), so
  // prefixing a value that already carries its own `data:` scheme yields
  // `data:image/png;base64,data:image/png;base64,…` — which the provider
  // rejects with the same "invalid base64-encoded value" 400 that the tagged
  // -shape handling above exists to prevent, just reached by another route.
  if (typeof data === 'string') {
    const trimmed = data.trim()
    if (trimmed.startsWith('data:') || /^https?:\/\//i.test(trimmed)) {
      return trimmed
    }
  }

  return `data:${mediaType};base64,${convertToBase64(data)}`
}

/**
 * Serialize a tool payload for the wire. Tool results (and, defensively,
 * tool-call arguments) are produced by tool code and can smuggle a cyclic
 * object — an error with req/res references, a fetch Response, a custom
 * tool's return value — into the transcript. Plain JSON.stringify would
 * throw and kill the whole LLM request; the fallback replacer breaks true
 * cycles only (shared non-cyclic references are preserved), marking repeated
 * ancestors with `[Circular]` so the model still receives the data.
 * Non-cycle failures (BigInt, throwing toJSON) rethrow unchanged.
 */
function stringifyToolPayload(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    // Only rescue cycle errors; anything else keeps its previous behavior.
    if (!/cyclic|circular/i.test(message)) throw error
    const ancestors: unknown[] = []
    return JSON.stringify(value, function (this: unknown, _key, val) {
      if (ancestors.length > 0) {
        const thisPos = ancestors.indexOf(this)
        if (thisPos !== -1) {
          ancestors.splice(thisPos + 1)
        } else {
          ancestors.push(this)
        }
        if (
          typeof val === 'object' &&
          val !== null &&
          ancestors.includes(val)
        ) {
          return '[Circular]'
        }
      } else {
        ancestors.push(val)
      }
      return val
    })
  }
}

export function convertToOpenAICompatibleChatMessages(
  prompt: LanguageModelV2Prompt,
  options?: { providerOptionsName?: string; modelId?: string },
): OpenAICompatibleChatPrompt {
  const messages: OpenAICompatibleChatPrompt = []
  // Reasoning seen on the most recent assistant message, carried forward to
  // later tool-call-only assistant messages so thinking-mode providers
  // (DeepSeek V4) accept the replay.
  let lastReasoningContent = ''
  let lastReasoningDetails: JSONValue[] = []
  // DeepSeek V4 runs in thinking mode by default and rejects any assistant
  // message that omits `reasoning_content` once reasoning has entered the
  // conversation: "The `reasoning_content` in the thinking mode must be
  // passed back to the API." The field must be PRESENT on every assistant
  // message — the step's own reasoning when it reasoned, else the most
  // recent reasoning, else an empty string. `undefined` serializes to an
  // omitted field and DeepSeek 400s on it.
  const isDeepSeek =
    options?.modelId?.toLowerCase().includes('deepseek') ?? false
  for (const { role, content, ...message } of prompt) {
    const metadata = getOpenAIMetadata({ ...message })
    switch (role) {
      case 'system': {
        messages.push({ role: 'system', content, ...metadata })
        break
      }

      case 'user': {
        messages.push({
          role: 'user',
          content: content.map((part) => {
            const partMetadata = getOpenAIMetadata(part)
            switch (part.type) {
              case 'text': {
                return { type: 'text', text: part.text, ...partMetadata }
              }
              case 'file': {
                if (
                  part.mediaType === 'image' ||
                  part.mediaType.startsWith('image/')
                ) {
                  const mediaType =
                    part.mediaType === 'image' || part.mediaType === 'image/*'
                      ? 'image/jpeg'
                      : part.mediaType

                  return {
                    type: 'image_url',
                    image_url: {
                      url: imageUrlFromData(part.data, mediaType),
                    },
                    ...partMetadata,
                  }
                } else {
                  throw new UnsupportedFunctionalityError({
                    functionality: `file part media type ${part.mediaType}`,
                  })
                }
              }
            }
          }),
          ...metadata,
        })

        break
      }

      case 'assistant': {
        let text = ''
        let reasoningContent = ''
        const reasoningDetails: JSONValue[] = []
        const toolCalls: Array<{
          id: string
          type: 'function'
          function: { name: string; arguments: string }
        }> = []

        for (const part of content) {
          const partMetadata = getOpenAIMetadata(part)
          switch (part.type) {
            case 'text': {
              text += part.text
              break
            }
            case 'reasoning': {
              // A reasoning part can carry only providerMetadata
              // (reasoning_details / signatures) with no text — e.g. the
              // reasoning-end chunk of a redacted-thinking response. Guard so
              // `'' + undefined` cannot produce the literal string
              // "undefined" in reasoning_content.
              if (typeof part.text === 'string') {
                reasoningContent += part.text
              }
              // Replay OpenRouter reasoning blocks (carrying the provider's
              // thinking signatures) captured on the response — see the
              // language model's reasoning_details assembly. Signatures are
              // only valid for the model that produced them, so blocks
              // captured from a different model (fallback, mid-thread model
              // switch) are dropped rather than replayed.
              const namespaces = options?.providerOptionsName
                ? [part.providerOptions?.[options.providerOptionsName]]
                : Object.values(part.providerOptions ?? {})
              for (const namespace of namespaces) {
                const details = namespace?.reasoning_details
                if (!Array.isArray(details)) continue
                const detailsModel = namespace?.model
                if (
                  typeof detailsModel === 'string' &&
                  options?.modelId !== undefined &&
                  detailsModel !== options.modelId
                ) {
                  continue
                }
                reasoningDetails.push(...details)
              }
              break
            }
            case 'tool-call': {
              toolCalls.push({
                id: part.toolCallId,
                type: 'function',
                function: {
                  name: part.toolName,
                  arguments: stringifyToolPayload(part.input),
                },
                ...partMetadata,
              })
              break
            }
          }
        }

        // Emit one wire message per run of assistant messages. Thinking models
        // that validate tool-call replay (e.g. DeepSeek V4) require the step's
        // reasoning_content to sit ON the message carrying tool_calls — a
        // separate adjacent assistant message fails the request — so merge
        // instead of pushing a second assistant message.
        const previous = messages[messages.length - 1]
        if (previous?.role === 'assistant') {
          if (text.length > 0) {
            previous.content =
              typeof previous.content === 'string'
                ? previous.content + text
                : text
          }
          // Always keep reasoning_content when the step produced reasoning
          // text: providers that validate replay (DeepSeek V4) require it on
          // the message, and it is the only reasoning field those APIs
          // understand. reasoning_details additionally carry the provider's
          // signatures for providers that use them — emit both, never either
          // in place of the other.
          if (reasoningContent.length > 0) {
            previous.reasoning_content =
              typeof previous.reasoning_content === 'string'
                ? previous.reasoning_content + reasoningContent
                : reasoningContent
            lastReasoningContent = previous.reasoning_content
          }
          if (reasoningDetails.length > 0) {
            previous.reasoning_details = [
              ...(previous.reasoning_details ?? []),
              ...reasoningDetails,
            ]
            lastReasoningDetails = previous.reasoning_details
          }
          if (toolCalls.length > 0) {
            previous.tool_calls = [...(previous.tool_calls ?? []), ...toolCalls]
          }
          // DeepSeek requires reasoning_content on every assistant message:
          // ensure the merged message carries one (the step's, or the most
          // recent carried-forward reasoning).
          if (
            isDeepSeek &&
            (previous.reasoning_content === undefined ||
              previous.reasoning_content === 'undefined')
          ) {
            previous.reasoning_content = lastReasoningContent
          }
          // Metadata unions with later-wins precedence — the same key
          // precedence the push path gets from spreading metadata last.
          Object.assign(previous, metadata)
          break
        }

        // Thinking models (DeepSeek V4) reject an assistant message that omits
        // reasoning_content once the conversation is in thinking mode: "The
        // `reasoning_content` in the thinking mode must be passed back to the
        // API." A later tool-call step can stream tool_calls without producing
        // fresh reasoning, so carry forward the most recent reasoning so the
        // turn stays replay-valid.
        const effectiveReasoningContent =
          reasoningContent.length > 0
            ? reasoningContent
            : isDeepSeek
              ? lastReasoningContent
              : toolCalls.length > 0
                ? lastReasoningContent
                : ''
        const effectiveReasoningDetails =
          reasoningDetails.length > 0
            ? reasoningDetails
            : toolCalls.length > 0
              ? lastReasoningDetails
              : []
        if (effectiveReasoningContent.length > 0) {
          lastReasoningContent = effectiveReasoningContent
        }
        if (effectiveReasoningDetails.length > 0) {
          lastReasoningDetails = effectiveReasoningDetails
        }

        messages.push({
          role: 'assistant',
          content: text,
          reasoning_content: isDeepSeek
            ? effectiveReasoningContent.length > 0
              ? effectiveReasoningContent
              : ''
            : effectiveReasoningContent.length > 0
              ? effectiveReasoningContent
              : undefined,
          reasoning_details:
            effectiveReasoningDetails.length > 0
              ? effectiveReasoningDetails
              : undefined,
          tool_calls: toolCalls.length > 0 ? toolCalls : undefined,
          ...metadata,
        })

        break
      }

      case 'tool': {
        for (const toolResponse of content) {
          const output = toolResponse.output

          let contentValue: string
          switch (output.type) {
            case 'text':
            case 'error-text':
              contentValue = output.value
              break
            case 'content':
            case 'json':
            case 'error-json':
              contentValue = stringifyToolPayload(output.value)
              break
          }

          const toolResponseMetadata = getOpenAIMetadata(toolResponse)
          messages.push({
            role: 'tool',
            tool_call_id: toolResponse.toolCallId,
            content: contentValue,
            ...toolResponseMetadata,
          })
        }
        break
      }

      default: {
        const _exhaustiveCheck: never = role
        throw new Error(`Unsupported role: ${_exhaustiveCheck}`)
      }
    }
  }

  return messages
}
